import { type AnchorHTMLAttributes, type HTMLAttributes, type MouseEvent } from 'react';
import { type To } from 'react-router';

import LinkExternalIcon from '@/assets/svg/bx-link-external.svg?react';
import Icon, { type IconSizeType } from '@/components/icon';
import { InstanceLink } from '@/components/routing';
import { cn } from '@/lib/utils';

import { Text } from '../Text';
import { type LinkColorType, type TextVariantType } from '../types';

type LinkHTMLProps = HTMLAttributes<HTMLElement> & AnchorHTMLAttributes<HTMLElement>;

type BaseLinkProps = Omit<LinkHTMLProps, 'href'> & {
  className?: string;
  to?: To;
  variant?: TextVariantType;
  color?: LinkColorType;
  weight?: 'regular' | 'medium' | 'semi-bold' | 'bold';
  disabled?: boolean;
  showExternalIcon?: boolean;
  onClick?: (e?: MouseEvent) => void;
};

export type LinkProps = BaseLinkProps &
  (
    | {
        to?: To;
        previousPage?: never;
      }
    | {
        to?: never;
        previousPage?: boolean;
      }
  );

const getDefaultWeight = (color: LinkColorType): 'regular' | 'semi-bold' => {
  switch (color) {
    case 'aqua':
    case 'static-white':
      return 'semi-bold';
    default:
      return 'regular';
  }
};

const getIconSize = (variant: TextVariantType): IconSizeType => {
  switch (variant) {
    case 'headline':
      return 'small';
    case 'body2':
    case 'footnote':
    case 'caption1':
    case 'caption2':
      return 'xxsmall';
    default:
      return 'xsmall';
  }
};

const variantClasses: Record<TextVariantType, string> = {
  kpi: cn('text-2xl leading-8 tracking-[-0.5px]'),
  headline: cn('text-xl leading-6 tracking-[-0.5px]'),
  subhead: cn('text-base leading-5 tracking-[-0.03em]'),
  label: cn('text-base leading-5 tracking-[-0.03em]'),
  body1: cn('text-base leading-5 tracking-[-0.5px]'),
  body2: cn('text-sm leading-5 tracking-[-0.5px]'),
  footnote: cn('text-sm leading-5 tracking-[-0.03em]'),
  caption1: cn('text-xs leading-4 tracking-[-0.41px]'),
  caption2: cn('text-xxs leading-3 tracking-[-0.41px]'),
};

const weightClasses: Record<string, string> = {
  regular: cn('font-normal'),
  medium: cn('font-medium'),
  'semi-bold': cn('font-semibold'),
  bold: cn('font-bold'),
};

const colorClasses: Record<LinkColorType, string> = {
  black: cn(
    'text-gray-900! outline-none focus-within:text-green-500! hover:text-green-500! focus:text-green-500! focus-visible:text-green-500! active:text-green-600!',
  ),
  aqua: cn(
    'text-aqua-400! outline-none focus-within:text-aqua-500! hover:text-aqua-500! focus:text-aqua-500! focus-visible:text-aqua-500! active:text-aqua-600!',
  ),
  'static-black': cn('text-gray-900! focus-visible:underline focus-visible:decoration-double'),
  'static-white': cn('text-white! focus-visible:underline focus-visible:decoration-double'),
  'static-grey': cn('text-gray-600! focus-visible:underline focus-visible:decoration-double'),
  'static-green': cn('text-green-400! focus-visible:underline focus-visible:decoration-double'),
  'static-red': cn('text-red-300! focus-visible:underline focus-visible:decoration-double'),
};

const Link = ({
  className,
  variant = 'body1',
  color = 'black',
  weight,
  to,
  target,
  rel,
  previousPage,
  children,
  disabled = false,
  showExternalIcon = false,
  onClick,
  ...rest
}: LinkProps) => {
  const linkBaseStyles = cn(
    'inline-flex cursor-pointer items-center gap-1 font-sans underline! underline-offset-2 outline-none',
    colorClasses[color],
    weightClasses[weight ?? getDefaultWeight(color)],
    disabled && 'cursor-not-allowed text-gray-400!',
    className,
  );

  if (to) {
    return (
      <InstanceLink
        {...rest}
        className={cn(linkBaseStyles, variantClasses[variant])}
        to={to}
        tabIndex={disabled ? -1 : 0}
        target={target}
        rel={!rel && target === '_blank' ? 'noreferrer' : rel}
        onClick={(e: MouseEvent<HTMLAnchorElement>) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          onClick?.(e);
        }}
      >
        {children}
        {showExternalIcon && <Icon size={getIconSize(variant)} icon={LinkExternalIcon} />}
      </InstanceLink>
    );
  }

  return (
    <Text
      {...rest}
      className={linkBaseStyles}
      variant={variant}
      onClick={previousPage ? () => history.back() : (e) => onClick?.(e)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          onClick?.();
          if (previousPage) {
            history.back();
          }
        }
      }}
      role="link"
      tabIndex={disabled ? undefined : 0}
    >
      {children}
      {showExternalIcon && <Icon size={getIconSize(variant)} icon={LinkExternalIcon} />}
    </Text>
  );
};

export default Link;
